import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, IconButton, MenuItem, Stack, TextField, Typography} from 'views/components';
import {ArrowForwardIos, ArrowBackIosNewRounded} from 'views/icons';
import {Translate, translate} from 'views/module';

const ScheduleEventEditOncall = (props) => {
  const {initialEvent, onSave, onRemove} = props;

  const [visitLength, setVisitLength] = React.useState('');

  const visitLengthOptions = React.useMemo(() => ([
    {id: 'A_FEW_HOURS', labelExpression: 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.FewHours'},
    {id: 'HALF_DAY', labelExpression: 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.HalfDay'},
    {id: 'A_FULL_DAY', labelExpression: 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.FullDay'},
    {id: 'OVERNIGHT', labelExpression: 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.Overnight'},
    {id: 'NOT_SURE_YET', labelExpression: 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.NotSureYetOrByTheHour'},
  ]), []);
  const visitLengthLabelExpressionById = React.useMemo(() => ({
    'A_FEW_HOURS': 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.FewHours',
    'HALF_DAY': 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.HalfDay',
    'A_FULL_DAY': 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.FullDay',
    'OVERNIGHT': 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.Overnight',
    'NOT_SURE_YET': 'schedulestep.ScheduleEventEditOncall.VisitLengthOptions.NotSureYetOrByTheHour',
  }), []);

  const resetFromInitialEvent = React.useCallback(() => {
    if (!initialEvent) {
      setVisitLength('');
      return;
    }

    setVisitLength(`${initialEvent.visitLength}`);
  }, [initialEvent]);

  React.useEffect(() => {
    resetFromInitialEvent();
  }, [resetFromInitialEvent]);

  const handleSave = React.useCallback(() => {
    const nextEvent = {
      scheduleType: 'ON_CALL',
      visitLength: `${visitLength}`.trim(),
    };

    onSave(nextEvent);
  }, [onSave, visitLength]);

  return (
    <Box
      sx={{
        border: '1px dashed #C5BBB9',
        borderRadius: '24px',
        p: '32px',
        backgroundColor: 'white',
      }}
    >
      <Stack sx={{gap: '32px'}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <IconButton
              onClick={onRemove}
              aria-label={translate('schedulestep.ScheduleEventEditOncall.RemoveAriaLabel')}
              sx={{
                'border': '1px solid #D4C8C2',
                'borderRadius': '8px',
                'color': '#5E5855',
                'p': '6px',
                '&:hover': {
                  borderColor: '#BDAFA8',
                  backgroundColor: '#F7F3F0',
                },
              }}
            >
              <ArrowBackIosNewRounded sx={{fontSize: 24}} />
            </IconButton>
            <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
              <Translate expression={'schedulestep.ScheduleEventEditOncall.Title'} />
            </Typography>
          </Box>
          <Box sx={{border: '1px solid #C5BBB9', borderRadius: '22px', px: '16px', py: '8px'}}>
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#3C3C3C', lineHeight: '128%', whiteSpace: 'nowrap'}}>
              <Translate expression={'schedulestep.ScheduleEventEditOncall.StepLabel'} />
            </Typography>
          </Box>
        </Box>

        <Box sx={{gap: '16px', display: 'flex', flexDirection: 'column'}}>
          <Box sx={{p: '24px', borderRadius: '16px', backgroundColor: '#F6F2EF'}}>
            <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#2D2925', lineHeight: '150%'}}>
              <Translate expression={'schedulestep.ScheduleEventEditOncall.Description'} />
            </Typography>
          </Box>

          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: '16px', fontWeight: 590, color: '#2D2925', lineHeight: '150%', mb: '8px'}}>
              <Translate expression={'schedulestep.ScheduleEventEditOncall.TypicalVisitLength'} />
            </Typography>
            <TextField
              fullWidth
              select
              value={visitLength}
              onChange={(value) => setVisitLength(value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) {
                    return translate('schedulestep.ScheduleEventEditOncall.VisitLengthPlaceholder');
                  }
                  const labelExpression = visitLengthLabelExpressionById[value];
                  return labelExpression ? translate(labelExpression) : value;
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  py: '14px',
                  px: '12px',
                },
                '& .MuiSelect-select': {
                  color: visitLength ? '#2D2925' : '#A69E9A',
                },
              }}
            >
              <MenuItem value=''>
                <Translate expression={'schedulestep.ScheduleEventEditOncall.VisitLengthPlaceholder'} />
              </MenuItem>
              {
                visitLengthOptions.map((visitLengthOption) => (
                  <MenuItem key={visitLengthOption.id} value={visitLengthOption.id}>
                    <Translate expression={visitLengthOption.labelExpression} />
                  </MenuItem>
                ))
              }
            </TextField>
          </Box>
        </Box>

        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            onClick={handleSave}
            variant='contained'
            endIcon={<ArrowForwardIos />}
            sx={{
              'borderRadius': '12px',
              'textTransform': 'none',
              'backgroundColor': '#DA7955',
              'fontSize': '16px',
              'fontWeight': 500,
              'lineHeight': '150%',
              'color': '#F6F2EF',
              'px': '20px',
              'py': '14px',
              'gap': '4px',
              '&:hover': {backgroundColor: '#C86945'},
            }}
          >
            <Translate expression={'schedulestep.ScheduleEventEditOncall.SaveButton'} />
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

ScheduleEventEditOncall.propTypes = {
  initialEvent: PropTypes.shape({
    visitLength: PropTypes.string,
  }),
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
};

export default React.memo(ScheduleEventEditOncall);
